import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { 
  Search, 
  Download, 
  Users, 
  TrendingUp, 
  Clock,
  ChevronRight,
  LogOut,
  Filter,
  X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [notesUpdate, setNotesUpdate] = useState('');
  const [fetchError, setFetchError] = useState('');

  const fetchLeads = useCallback(async () => {
    try {
      setFetchError('');
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data || []);
    } catch (error) {
      // Error handled by fetchError state
      if (error.response?.status === 401) {
        navigate('/admin/login');
        return;
      }
      setFetchError(
        error.response?.data?.detail || 
        error.message || 
        'Failed to load leads. Check your connection and try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const filterLeads = useCallback(() => {
    let filtered = [...leads];

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(lead => 
        (lead.quiz_responses?.first_name || '').toLowerCase().includes(q) ||
        (lead.quiz_responses?.email || '').toLowerCase().includes(q) ||
        (lead.quiz_responses?.phone || '').includes(searchQuery)
      );
    }

    // Score filter
    if (scoreFilter !== 'all') {
      filtered = filtered.filter(lead => lead.lead_score === scoreFilter);
    }

    setFilteredLeads(filtered);
  }, [leads, searchQuery, scoreFilter]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchLeads();
  }, [navigate, fetchLeads]);

  useEffect(() => {
    filterLeads();
  }, [filterLeads]);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      // Export error
    }
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/leads/${selectedLead.id}`,
        {
          status: statusUpdate || selectedLead.status,
          notes: notesUpdate || selectedLead.notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      fetchLeads();
      setSelectedLead(null);
      setStatusUpdate('');
      setNotesUpdate('');
    } catch (error) {
      // Update error
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const getScoreBadgeColor = (score) => {
    const colors = {
      hot: 'bg-red-500/20 text-red-400 border-red-500/50',
      warm: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      cold: 'bg-blue-500/20 text-blue-400 border-blue-500/50'
    };
    return colors[score] || colors.cold;
  };

  const stats = {
    total: leads.length,
    hot: leads.filter(l => l.lead_score === 'hot').length,
    warm: leads.filter(l => l.lead_score === 'warm').length,
    cold: leads.filter(l => l.lead_score === 'cold').length
  };

  const getLeadName = (lead) => lead?.quiz_responses?.first_name || lead?.quiz_responses?.name || 'Unknown';
  const getLeadEmail = (lead) => lead?.quiz_responses?.email || 'N/A';
  const getLeadPhone = (lead) => lead?.quiz_responses?.phone || 'N/A';
  const getLeadDate = (lead) => {
    const ts = lead?.timestamp || lead?.created_at;
    return ts ? new Date(ts).toLocaleDateString() : 'N/A';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#020817] text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#22D3EE] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-[#020817] text-foreground flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <X className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-2">Unable to Load Leads</h2>
          <p className="text-slate-400 mb-4">{fetchError}</p>
          <div className="flex flex-col gap-3">
            <Button
              onClick={fetchLeads}
              className="bg-primary text-primary-foreground hover:bg-cyan-300 rounded-full"
              data-testid="retry-btn"
            >
              Try Again
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              className="rounded-full"
              data-testid="fallback-export-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Leads as CSV
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full"
              data-testid="error-logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout & Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020817] text-foreground">
      {/* Header */}
      <header className="border-b border-white/5 backdrop-blur-xl bg-slate-950/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="font-heading text-2xl font-bold">Bryan 2.0 Admin</h1>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/blog')}
              variant="outline"
              className="rounded-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
              data-testid="blog-management-btn"
            >
              Blog
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full"
              data-testid="logout-btn"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-6"
            data-testid="stat-total"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="h-5 w-5 text-[#22D3EE]" />
              <span className="text-slate-400 text-sm">Total Leads</span>
            </div>
            <p className="text-3xl font-heading font-bold">{stats.total}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-6"
            data-testid="stat-hot"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-red-400" />
              <span className="text-slate-400 text-sm">Hot Leads</span>
            </div>
            <p className="text-3xl font-heading font-bold text-red-400">{stats.hot}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-6"
            data-testid="stat-warm"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-yellow-400" />
              <span className="text-slate-400 text-sm">Warm Leads</span>
            </div>
            <p className="text-3xl font-heading font-bold text-yellow-400">{stats.warm}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-6"
            data-testid="stat-cold"
          >
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-blue-400" />
              <span className="text-slate-400 text-sm">Cold Leads</span>
            </div>
            <p className="text-3xl font-heading font-bold text-blue-400">{stats.cold}</p>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-950/50 border-slate-800"
                  data-testid="search-leads-input"
                />
              </div>
            </div>

            <div className="flex gap-2">
              {['all', 'hot', 'warm', 'cold'].map((score) => (
                <Button
                  key={score}
                  onClick={() => setScoreFilter(score)}
                  variant={scoreFilter === score ? 'default' : 'outline'}
                  className="rounded-full capitalize"
                  data-testid={`filter-${score}-btn`}
                >
                  <Filter className="mr-2 h-4 w-4" />
                  {score}
                </Button>
              ))}
            </div>

            <Button
              onClick={handleExport}
              variant="outline"
              className="rounded-full"
              data-testid="export-csv-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Leads Table */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leads List */}
          <div className="space-y-4">
            <h2 className="font-heading text-xl font-bold mb-4">Leads ({filteredLeads.length})</h2>
            
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="leads-list">
              {filteredLeads.map((lead, idx) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => setSelectedLead(lead)}
                  className={`backdrop-blur-xl bg-slate-950/40 border rounded-xl p-4 cursor-pointer hover:bg-slate-900/60 ${
                    selectedLead?.id === lead.id ? 'border-[#22D3EE]' : 'border-white/10'
                  }`}
                  data-testid={`lead-card-${idx}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-lg">{getLeadName(lead)}</h3>
                      <p className="text-sm text-slate-400">{getLeadEmail(lead)}</p>
                    </div>
                    <Badge className={`${getScoreBadgeColor(lead.lead_score)} border`}>
                      {lead.lead_score || 'N/A'}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Clock className="h-3 w-3" />
                    {getLeadDate(lead)}
                  </div>
                  
                  <div className="mt-2">
                    <Badge variant="outline" className="text-xs">
                      {lead.status}
                    </Badge>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Lead Details */}
          <div>
            <h2 className="font-heading text-xl font-bold mb-4">Lead Details</h2>
            
            {selectedLead ? (
              <div className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-6" data-testid="lead-details">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="font-heading text-2xl font-bold mb-1">
                      {getLeadName(selectedLead)}
                    </h3>
                    <p className="text-slate-400">{getLeadEmail(selectedLead)}</p>
                    <p className="text-slate-400">{getLeadPhone(selectedLead)}</p>
                  </div>
                  <button
                    onClick={() => setSelectedLead(null)}
                    className="text-slate-400 hover:text-white"
                    data-testid="close-lead-details"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <Separator className="my-4" />

                <div className="space-y-4 text-sm">
                  <div>
                    <span className="text-slate-400">Age Range:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.age_range || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Height:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.height || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Current Weight:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.current_weight ? `${selectedLead.quiz_responses.current_weight} lbs` : 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Goal Weight:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.goal_weight ? `${selectedLead.quiz_responses.goal_weight} lbs` : 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Timeline:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.timeline || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Prior GLP-1:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.prior_glp1 || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Medication Interest:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.medication_interest || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Preference:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.preference || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Biggest Challenge:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.biggest_challenge || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Budget:</span>
                    <span className="ml-2 text-white">{selectedLead.quiz_responses?.budget || 'N/A'}</span>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-400 mb-2 block">Status</Label>
                    <select
                      value={statusUpdate || selectedLead.status}
                      onChange={(e) => setStatusUpdate(e.target.value)}
                      className="w-full bg-slate-950/50 border-slate-800 rounded-lg h-10 px-3 text-sm"
                      data-testid="status-select"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="closed">Closed</option>
                      <option value="no response">No Response</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm text-slate-400 mb-2 block">Notes</Label>
                    <textarea
                      value={notesUpdate || selectedLead.notes || ''}
                      onChange={(e) => setNotesUpdate(e.target.value)}
                      placeholder="Add notes..."
                      rows={4}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-lg p-3 text-sm resize-none"
                      data-testid="notes-textarea"
                    />
                  </div>

                  <Button
                    onClick={handleUpdateLead}
                    className="w-full bg-primary text-primary-foreground hover:bg-cyan-300 rounded-full"
                    data-testid="update-lead-btn"
                  >
                    Update Lead
                  </Button>
                </div>
              </div>
            ) : (
              <div className="backdrop-blur-xl bg-slate-950/40 border border-white/10 rounded-xl p-12 text-center">
                <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Select a lead to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;